import { createElement, useEffect } from 'react';
import { useGetCustomerQuery } from '../api';
import { modalManager } from '../utils/modalManager';
import { canResubmitInn, clearInnSubmission, getInnSubmission } from '../utils/innSubmission';
import { isTaxIdBlockingDue, isTaxIdReminderDue } from '../utils/taxIdIntro';
import InnModalContent from '../components/support/InnModalContent';

/**
 * Хук для проверки необходимости показа модалки с запросом ИНН
 * @param showModal - Показывать ли модалку автоматически (по умолчанию true)
 * @returns Объект с информацией о необходимости показа уведомления и функциями
 */
export const useInnNotification = (showModal: boolean = true) => {
    const { data: customer } = useGetCustomerQuery();

    // Мягкое напоминание — с 15 дня после регистрации
    const shouldShowNotification = (): boolean => isTaxIdReminderDue(customer);

    // Обязательная незакрываемая модалка — после дедлайна
    const shouldForceInnModal = (): boolean => isTaxIdBlockingDue(customer);

    const showInnModal = () => {
        modalManager.show({
            title: '',
            content: createElement(InnModalContent),
            size: 'md',
        });
    };

    // Обязательная незакрываемая модалка (после истечения отсрочки)
    const showInnRequiredModal = () => {
        modalManager.show({
            title: '',
            content: createElement(InnModalContent, { isRequired: true }),
            size: 'md',
            isCloseDisabled: true,
        });
    };

    const handleSubmitInn = () => {
        showInnModal();
    };

    // ИНН появился в профиле — проверка пройдена, сохраненное состояние больше не нужно
    useEffect(() => {
        if (customer?.id && customer.inn && customer.inn.trim() !== '') {
            clearInnSubmission(customer.id);
        }
    }, [customer?.id, customer?.inn]);

    // Автоматически показываем модалку при загрузке, если нужно
    useEffect(() => {
        if (!showModal) return;

        if (shouldForceInnModal()) {
            showInnRequiredModal();
        } else if (shouldShowNotification()) {
            // Мягкую модалку не показываем повторно, пока отправленный ИНН на проверке
            const submission = getInnSubmission(customer?.id);
            if (submission && !canResubmitInn(submission)) return;
            showInnModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        customer?.id,
        customer?.inn,
        customer?.created_at,
        customer?.tax_id_intro_deadline,
        customer?.tax_id_intro_delay_days,
        showModal,
    ]);

    return {
        isNeedRequestINN: shouldShowNotification(),
        isInnRequired: shouldForceInnModal(),
        showInnModal,
        showInnRequiredModal,
        handleSubmitInn,
    };
};
